import React, { useId, useMemo, useRef, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import sales from '../../Sales';
import { useAdmin } from '../adminContext';
import { amountInput, formatEuro, parseAmount, sortByName, sortServices, todayIso } from '../format';
import { useAdminData } from '../store';
import { Button, Combobox, EntryCard, Field, FormError, MoneyInput, Segmented } from '../ui';

const dealKinds = [
  { value: 1, label: 'Nieuw' },
  { value: 0, label: 'Bestaand' },
];

const blankFrom = (previous) => ({
  userId: previous?.userId || '',
  productId: '',
  price: '',
  date: previous?.date || todayIso(),
  business: '',
  status: previous?.status ?? 0,
});

const DealForm = ({ deal, onDone }) => {
  const isNew = !deal;
  const { notify, confirm, changed } = useAdmin();
  const id = useId();
  const productRef = useRef(null);

  const [form, setForm] = useState(() =>
    deal
      ? {
          userId: String(deal.user_id),
          productId: String(deal.product_id),
          price: amountInput(deal.price),
          date: deal.created_at || todayIso(),
          business: deal.business || '',
          status: Number(deal.status),
        }
      : blankFrom(),
  );
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');

  const filter = isNew ? '?filter=true' : '';
  const options = useAdminData({ users: `users${filter}`, products: `products${filter}` });

  const users = useMemo(() => sortByName(options.data?.users || []), [options.data]);
  const products = useMemo(() => sortServices(options.data?.products || []), [options.data]);
  const userOptions = useMemo(
    () => users.map((user) => ({ value: String(user.user_id), label: user.name })),
    [users],
  );
  const productOptions = useMemo(
    () => products.map((product) => ({ value: String(product.product_id), label: product.name })),
    [products],
  );

  const set = (key, value) => setForm((previous) => ({ ...previous, [key]: value }));

  const pickProduct = (productId) => {
    const product = products.find((item) => String(item.product_id) === productId);
    setForm((previous) => ({
      ...previous,
      productId,
      price: product?.price ? amountInput(product.price) : previous.price,
    }));
  };

  const submit = async () => {
    const user = users.find((item) => String(item.user_id) === form.userId);
    const product = products.find((item) => String(item.product_id) === form.productId);
    const price = parseAmount(form.price);

    if (!user || !product) {
      setProblem('Kies een verkoper en een dienst.');
      return;
    }
    if (price === null || Number.isNaN(price)) {
      setProblem('Vul een geldig bedrag in, bijvoorbeeld 1250 of 1250,50.');
      return;
    }
    if (!form.business.trim()) {
      setProblem('Vul het bedrijf in.');
      return;
    }

    const body = {
      user_id: user.user_id,
      product_id: product.product_id,
      price,
      created_at: form.date,
      business: form.business.trim(),
      status: form.status,
    };

    setBusy(true);
    setProblem('');
    try {
      if (isNew) {
        await sales.post('deals', body);
        notify(`Deal van ${user.name} toegevoegd, ${formatEuro(price)}`);
        setForm((previous) => blankFrom(previous));
        setTimeout(() => productRef.current?.focus(), 0);
      } else {
        await sales.put('deals', { deal_id: deal.deal_id, ...body });
        notify('Deal opgeslagen');
        onDone();
      }
      changed();
    } catch (error) {
      console.warn(error);
      setProblem('Opslaan is mislukt. Probeer het opnieuw.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    const confirmed = await confirm({
      title: 'Deal verwijderen?',
      text: `De deal bij ${deal.business || 'onbekend bedrijf'} (${formatEuro(deal.price)}) wordt verwijderd. Dit kun je niet ongedaan maken.`,
    });
    if (!confirmed) return;

    setBusy(true);
    try {
      await sales.delete(`deals?deal_id=${deal.deal_id}`);
      notify('Deal verwijderd');
      changed();
      onDone();
    } catch (error) {
      console.warn(error);
      notify('Verwijderen is mislukt', 'error');
      setBusy(false);
    }
  };

  const loadingOptions = !options.data;

  return (
    <EntryCard
      title={isNew ? 'Nieuwe deal' : 'Deal bewerken'}
      inline={!isNew}
      onSubmit={submit}
      onCancel={onDone}
      busy={busy}
      actions={
        <>
          {!isNew && (
            <Button variant="danger-text" icon={Trash2} onClick={remove} disabled={busy}>
              Verwijderen
            </Button>
          )}
          <span className="adm-grow" />
          {!isNew && (
            <Button variant="ghost" onClick={onDone} disabled={busy}>
              Annuleren
            </Button>
          )}
          <Button type="submit" icon={isNew ? Plus : undefined} disabled={busy || loadingOptions}>
            {busy ? 'Bezig...' : isNew ? 'Toevoegen' : 'Opslaan'}
          </Button>
        </>
      }
    >
      <div className="adm-deal-grid">
        <Field label="Verkoper" htmlFor={`${id}-user`}>
          <Combobox
            id={`${id}-user`}
            options={userOptions}
            value={form.userId}
            onChange={(value) => set('userId', value)}
            placeholder={loadingOptions ? 'Laden...' : 'Typ of kies'}
            disabled={loadingOptions}
          />
        </Field>
        <Field label="Dienst" htmlFor={`${id}-product`} className="adm-span-2">
          <Combobox
            id={`${id}-product`}
            inputRef={productRef}
            options={productOptions}
            value={form.productId}
            onChange={pickProduct}
            placeholder={loadingOptions ? 'Laden...' : 'Typ of kies'}
            disabled={loadingOptions}
          />
        </Field>
        <Field label="Datum" htmlFor={`${id}-date`}>
          <input
            id={`${id}-date`}
            className="adm-input"
            type="date"
            value={form.date}
            onChange={(event) => set('date', event.target.value)}
            required
          />
        </Field>
        <Field label="Bedrijf" htmlFor={`${id}-business`} className="adm-span-2">
          <input
            id={`${id}-business`}
            className="adm-input"
            type="text"
            value={form.business}
            onChange={(event) => set('business', event.target.value)}
            placeholder="Naam van de klant"
          />
        </Field>
        <Field label="Bedrag" htmlFor={`${id}-price`}>
          <MoneyInput
            id={`${id}-price`}
            value={form.price}
            onChange={(event) => set('price', event.target.value)}
            placeholder="0,00"
          />
        </Field>
        <div className="adm-field">
          <span className="adm-label">Soort deal</span>
          <Segmented
            label="Soort deal"
            options={dealKinds}
            value={form.status}
            onChange={(value) => set('status', value)}
          />
        </div>
      </div>
      <FormError>
        {problem || (options.error && !options.data && 'Verkopers en diensten konden niet worden geladen.')}
      </FormError>
    </EntryCard>
  );
};

export default DealForm;
